import React from "react";
import styled from "styled-components";
import {
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalSection,
} from "src/components";
import { EXTERNAL_CALENDAR_PROVIDERS } from "../calendarData";
import type { ExternalCalendarProvider, ThemeMode } from "../types";

const ProviderList = styled.div`
    display: grid;
    gap: 0.75rem;
`;

const ProviderButton = styled.button`
    width: 100%;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.85rem;
    padding: 0.9rem 1rem;
    border: 1px solid var(--calendar-border);
    border-radius: var(--calendar-radius-card);
    background: var(--calendar-surface-secondary);
    color: var(--calendar-text);
    cursor: pointer;
    text-align: left;

    &:hover,
    &:focus-visible {
        border-color: var(--calendar-accent);
        outline: none;
    }
`;

const ProviderColor = styled.span<{ color: string }>`
    width: 0.65rem;
    height: 0.65rem;
    border-radius: var(--calendar-radius-round);
    background: ${p => p.color};
`;

const ProviderName = styled.span`
    min-width: 0;
    font-size: 0.9375rem;
    font-weight: 700;
`;

const ProviderAction = styled.span`
    color: var(--calendar-accent);
    font-size: 0.8125rem;
    font-weight: 700;
`;

type ExternalCalendarProviderModalProps = {
    onClose: () => void;
    onSelect: (provider: ExternalCalendarProvider) => void;
    open: boolean;
    themeMode: ThemeMode;
};

export function ExternalCalendarProviderModal({
    onClose,
    onSelect,
    open,
    themeMode,
}: ExternalCalendarProviderModalProps) {
    return (
        <StandardModal
            description="Choose a provider to connect. You can connect more than one account from the same provider."
            footer={(
                <StandardModalActionGroup style={{ marginLeft: "auto" }}>
                    <StandardModalActionButton onClick={onClose}>Cancel</StandardModalActionButton>
                </StandardModalActionGroup>
            )}
            isDarkMode={themeMode === "dark"}
            onClose={onClose}
            open={open}
            title="Add external calendar"
            widthMode="small"
        >
            <StandardModalSection>
                <ProviderList>
                    {EXTERNAL_CALENDAR_PROVIDERS.map(provider => (
                        <ProviderButton
                            key={provider.provider}
                            onClick={() => onSelect(provider.provider)}
                            type="button"
                        >
                            <ProviderColor color={provider.color} />
                            <ProviderName>{provider.label}</ProviderName>
                            <ProviderAction>Connect</ProviderAction>
                        </ProviderButton>
                    ))}
                </ProviderList>
            </StandardModalSection>
        </StandardModal>
    );
}
